import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import axios from '../../../sharedModules/utils/axios.utils';

interface VendorContactMatch {
  id: number;
  full_name: string;
  email: string;
  u_vnddispl: string | null;
  vendor_id: string;
  location: string;
  contact_type: string;
}

interface WorkflowStatus {
  step_1_email_identity: {
    from_address: string | null;
    from_name: string | null;
    from_domain: string | null;
  };
  step_2_vendor_match: {
    matched: boolean;
    match_method: string | null;
    matched_contact: {
      id: number;
      full_name: string;
      email: string;
      vendor_id: string;
      location: string;
      contact_type: string;
    } | null;
    name_matches: VendorContactMatch[];
    email_matches: VendorContactMatch[];
    domain_matches: VendorContactMatch[];
  };
  step_3_vendor_contact_detail: {
    found: boolean;
    has_invoice_number_region: boolean;
    has_invoice_amount_region: boolean;
    vendor_contact_detail: {
      id: number;
      vendor_name: string;
      vendor_id: string;
      location: string;
    } | null;
  };
  step_4_textract_line_items: {
    has_invoices: boolean;
    has_line_items: boolean;
    invoices: Array<{
      id: number;
      statement_number: string | null;
      line_items_total: number | null;
      extraction_method: string | null;
      has_line_items: boolean;
      line_item_count: number;
    }>;
  };
  step_5_valid_extraction: {
    has_valid_extraction: boolean;
    invoices: Array<{
      id: number;
      statement_number: string | null;
      line_items_total: number | null;
      extraction_method: string | null;
      has_line_items: boolean;
      line_item_count: number;
    }>;
  };
}

interface VendorContactOption {
  id: number;
  full_name: string;
  email: string;
  vendor_id: string;
  vendor_name: string | null;
  location: string;
  contact_type: string;
}

type StepStatus = 'pass' | 'fail' | 'na';

const PassFail: React.FC<{ pass: boolean }> = ({ pass }) =>
  pass ? (
    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
  ) : (
    <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
  );

const SectionHeader: React.FC<{ title: string; status: StepStatus; label: string }> = ({ title, status, label }) => {
  const color = status === 'pass' ? 'success' : status === 'fail' ? 'error' : 'default';
  return (
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      <PassFail pass={status === 'pass'} />
      <Typography variant="subtitle2">{title}</Typography>
      <Chip label={label} color={color} size="small" variant="outlined" />
    </Box>
  );
};

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
    {children}
  </Typography>
);

const MatchTable: React.FC<{ label: string; matches: VendorContactMatch[] }> = ({ label, matches }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        {label}
      </Typography>
      <Chip label={matches.length} size="small" variant="outlined" color={matches.length > 0 ? 'success' : 'default'} />
    </Box>
    {matches.length > 0 ? (
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 1, fontSize: '0.75rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell>u_vnddispl</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Vendor ID</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matches.map((m) => (
              <TableRow key={m.id}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{m.u_vnddispl || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{m.email}</TableCell>
                <TableCell>{m.vendor_id}</TableCell>
                <TableCell>{m.location}</TableCell>
                <TableCell>{m.contact_type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        No matches
      </Typography>
    )}
  </Box>
);

interface Props {
  emailId: string;
}

const InvoiceWorkflowTracker: React.FC<Props> = ({ emailId }) => {
  const [status, setStatus] = useState<WorkflowStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [contactOptions, setContactOptions] = useState<VendorContactOption[]>([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [contactSearchInput, setContactSearchInput] = useState('');

  const [downloading, setDownloading] = useState(false);
  const [downloadResult, setDownloadResult] = useState<{
    filename: string;
    s3_url: string;
    vendor_name: string | null;
    vendor_id: string | null;
    location: string | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [regionInfoCopied, setRegionInfoCopied] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      try {
        const response: any = await axios.get(`/vendors/outlook_emails/${emailId}/workflow_status`);
        setStatus(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to load workflow status');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [emailId]);

  const searchContacts = useCallback(async (search: string) => {
    if (search.length < 2) {
      setContactOptions([]);
      return;
    }
    setContactSearchLoading(true);
    try {
      const response: any = await axios.get('/vendors/outlook_emails/vendor_contacts_search', {
        params: { search },
      });
      setContactOptions(response.data);
    } catch {
      setContactOptions([]);
    } finally {
      setContactSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchInput.length >= 2) {
        searchContacts(contactSearchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearchInput, searchContacts]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    setDownloadResult(null);
    try {
      const response: any = await axios.post(`/vendors/outlook_emails/${emailId}/download_statement_pdf`);
      setDownloadResult({
        filename: response.data.filename,
        s3_url: response.data.s3_url,
        vendor_name: response.data.vendor_name,
        vendor_id: response.data.vendor_id,
        location: response.data.location,
      });
    } catch {
      setDownloadResult(null);
    } finally {
      setDownloading(false);
    }
  };

  const buildRegionDetectionInfo = (): string => {
    if (!downloadResult) return '';
    const lines: string[] = [];
    lines.push(`Run /detect-regions for this vendor statement:`);
    lines.push(`- Vendor Name: ${downloadResult.vendor_name || '(unknown)'}`);
    lines.push(`- Vendor ID: ${downloadResult.vendor_id || '(unknown)'}`);
    lines.push(`- Location: ${downloadResult.location || '(unknown)'}`);
    lines.push(`- Statement PDF: ${downloadResult.s3_url}`);
    return lines.join('\n');
  };

  const handleCopyRegionInfo = async () => {
    const info = buildRegionDetectionInfo();
    await navigator.clipboard.writeText(info);
    setRegionInfoCopied(true);
    setTimeout(() => setRegionInfoCopied(false), 2000);
  };

  const buildDiagnosticsReport = (ws: WorkflowStatus): string => {
    const s1 = ws.step_1_email_identity;
    const s2 = ws.step_2_vendor_match;
    const s3 = ws.step_3_vendor_contact_detail;
    const s4 = ws.step_4_textract_line_items;
    const s5 = ws.step_5_valid_extraction;

    const pass = (b: boolean) => (b ? 'PASS' : 'FAIL');
    const lines: string[] = [];

    lines.push(`=== Outlook Email Diagnostics (ID: ${emailId}) ===`);
    lines.push('');

    // Step 1
    lines.push(`--- Step 1: Email Sender Identity [${pass(!!s1.from_address)}] ---`);
    lines.push(`from_name: ${s1.from_name || '(empty)'}`);
    lines.push(`from_address: ${s1.from_address || '(empty)'}`);
    lines.push(`from_domain: ${s1.from_domain || '(empty)'}`);
    lines.push('');

    // Step 2
    const matchLabel =
      s2.match_method === 'exact_email'
        ? 'exact email'
        : s2.match_method === 'display_name'
          ? 'display name'
          : s2.match_method === 'domain'
            ? 'domain'
            : 'none';
    lines.push(`--- Step 2: Vendor Contact Match [${pass(s2.matched)}] (${matchLabel}) ---`);
    if (s2.matched_contact) {
      lines.push(`Matched Contact:`);
      lines.push(`  full_name: ${s2.matched_contact.full_name}`);
      lines.push(`  email: ${s2.matched_contact.email}`);
      lines.push(`  vendor_id: ${s2.matched_contact.vendor_id}`);
      lines.push(`  location: ${s2.matched_contact.location}`);
      lines.push(`  contact_type: ${s2.matched_contact.contact_type}`);
    } else {
      lines.push(`Matched Contact: none`);
    }

    const fmtMatches = (label: string, matches: VendorContactMatch[]) => {
      lines.push(`${label}: ${matches.length} result(s)`);
      matches.forEach((m) => {
        lines.push(
          `  - ${m.u_vnddispl || '(no display name)'} | ${m.email} | ${m.vendor_id} | ${m.location} | ${m.contact_type}`,
        );
      });
    };
    fmtMatches(`Name matches (from_name → u_vnddispl)`, s2.name_matches);
    fmtMatches(`Email matches (from_address → email)`, s2.email_matches);
    fmtMatches(`Domain matches (@${s1.from_domain || '?'} → email domain)`, s2.domain_matches);
    lines.push('');

    // Step 3
    const s3Status = s3.found && s3.has_invoice_number_region && s3.has_invoice_amount_region;
    lines.push(`--- Step 3: Vendor Contact Detail [${pass(s3Status)}] ---`);
    lines.push(`Found: ${s3.found ? 'Yes' : 'No'}`);
    if (s3.vendor_contact_detail) {
      lines.push(`  vendor_name: ${s3.vendor_contact_detail.vendor_name}`);
      lines.push(`  vendor_id: ${s3.vendor_contact_detail.vendor_id}`);
      lines.push(`  location: ${s3.vendor_contact_detail.location}`);
      lines.push(`  has_invoice_number_region: ${s3.has_invoice_number_region}`);
      lines.push(`  has_invoice_amount_region: ${s3.has_invoice_amount_region}`);
    } else {
      lines.push(
        `Note: ${s2.matched ? 'Matched vendor has no VendorContactDetail record.' : 'No vendor match in step 2, so no detail lookup attempted.'}`,
      );
    }
    lines.push('');

    // Step 4
    lines.push(`--- Step 4: Textract Line Items [${pass(s4.has_line_items)}] ---`);
    lines.push(`Invoices created: ${s4.invoices.length}`);
    if (s4.invoices.length > 0) {
      s4.invoices.forEach((inv) => {
        lines.push(
          `  VendorInvoice #${inv.id}: ${inv.line_item_count} line items | method: ${inv.extraction_method || 'unknown'} | statement_number: ${inv.statement_number || '(none)'} | total_amount: ${inv.line_items_total != null ? `$${parseFloat(String(inv.line_items_total)).toFixed(2)}` : '(none)'}`,
        );
      });
    } else {
      lines.push(`Note: No vendor statements have been created from this email.`);
    }
    lines.push('');

    // Step 5
    const s5Status = s5.has_valid_extraction ? 'PASS' : s4.has_invoices ? 'FAIL' : 'N/A';
    lines.push(`--- Step 5: Valid Statement Number & Amount [${s5Status}] ---`);
    if (s5.invoices.length > 0) {
      s5.invoices.forEach((inv) => {
        lines.push(
          `  VendorInvoice #${inv.id}: statement_number=${inv.statement_number || '(none)'} | total_amount=${inv.line_items_total != null ? `$${parseFloat(String(inv.line_items_total)).toFixed(2)}` : '(none)'} | has_line_items=${inv.has_line_items} (${inv.line_item_count})`,
        );
      });
    } else {
      lines.push(`Note: No invoices to validate.`);
    }
    lines.push('');

    // Summary
    const statuses = [!!s1.from_address, s2.matched, s3Status, s4.has_line_items, s5.has_valid_extraction];
    const passCount = statuses.filter(Boolean).length;
    lines.push(`Summary: ${passCount}/5 steps passed`);

    return lines.join('\n');
  };

  const handleCopyDiagnostics = async () => {
    if (!status) return;
    const report = buildDiagnosticsReport(status);
    await navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading workflow status...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !status) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="error">
            {error || 'No workflow data available'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const s1 = status.step_1_email_identity;
  const s2 = status.step_2_vendor_match;
  const s3 = status.step_3_vendor_contact_detail;
  const s4 = status.step_4_textract_line_items;
  const s5 = status.step_5_valid_extraction;

  const step1Status: StepStatus = s1.from_address ? 'pass' : 'fail';
  const step2Status: StepStatus = s2.matched ? 'pass' : 'fail';
  const step3Status: StepStatus =
    s3.found && s3.has_invoice_number_region && s3.has_invoice_amount_region ? 'pass' : 'fail';
  const step4Status: StepStatus = s4.has_line_items ? 'pass' : 'fail';
  const step5Status: StepStatus = s5.has_valid_extraction ? 'pass' : s4.has_invoices ? 'fail' : 'na';

  const statuses = [step1Status, step2Status, step3Status, step4Status, step5Status];
  const passCount = statuses.filter((s) => s === 'pass').length;

  const matchMethodLabel = (method: string | null) => {
    switch (method) {
      case 'exact_email':
        return 'Exact email match';
      case 'display_name':
        return 'Display name match';
      case 'domain':
        return 'Domain match';
      default:
        return 'No match';
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Invoice Verification Workflow</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyDiagnostics}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Copied!' : 'Copy Diagnostics'}
            </Button>
            <Chip
              label={`${passCount}/5 steps passed`}
              color={passCount === 5 ? 'success' : passCount >= 3 ? 'warning' : 'error'}
              size="small"
            />
          </Box>
        </Box>

        {/* 1. Email Sender Identity */}
        <Box>
          <SectionHeader
            title="Email Sender Identity"
            status={step1Status}
            label={step1Status === 'pass' ? 'Found' : 'Missing'}
          />
          <Mono>
            {s1.from_name || '(no name)'} &lt;{s1.from_address || '(no email)'}&gt;
          </Mono>
        </Box>

        <Divider />

        {/* 2. Vendor Contact Match */}
        <Box>
          <SectionHeader
            title="Vendor Contact Match"
            status={step2Status}
            label={s2.matched ? matchMethodLabel(s2.match_method) : 'No match'}
          />
          <Box sx={{ mt: 1 }}>
            <MatchTable
              label={`Name Match — from_name "${s1.from_name || ''}" = u_vnddispl`}
              matches={s2.name_matches}
            />
            <MatchTable
              label={`Email Match — from_email "${s1.from_address || ''}" = email`}
              matches={s2.email_matches}
            />
            <MatchTable
              label={`Domain Match — domain "${s1.from_domain || ''}" ~ email domain`}
              matches={s2.domain_matches}
            />
          </Box>

          {!s2.matched && (
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Search for a vendor contact manually:
              </Typography>
              <Autocomplete
                size="small"
                options={contactOptions}
                loading={contactSearchLoading}
                getOptionLabel={(option) =>
                  `${option.vendor_name || option.vendor_id} — ${option.full_name} <${option.email}>`
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.vendor_name || option.vendor_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.full_name} &lt;{option.email}&gt; &middot; {option.location} &middot;{' '}
                        {option.contact_type}
                      </Typography>
                    </Box>
                  </li>
                )}
                onInputChange={(_event, value) => setContactSearchInput(value)}
                onChange={(_event, _value) => {
                  // Display-only for now — manual override would need a separate API
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by vendor name, email, or vendor ID..."
                    variant="outlined"
                  />
                )}
                noOptionsText={contactSearchInput.length < 2 ? 'Type at least 2 characters...' : 'No contacts found'}
                sx={{ maxWidth: 500 }}
              />
            </Box>
          )}
        </Box>

        <Divider />

        {/* 3. Vendor Contact Detail (Extraction Regions) */}
        <Box>
          <SectionHeader
            title="Vendor Contact Detail (Extraction Regions)"
            status={step3Status}
            label={
              !s3.found
                ? 'Not found'
                : s3.has_invoice_number_region && s3.has_invoice_amount_region
                  ? 'Both regions'
                  : 'Missing regions'
            }
          />
          {s3.found && (
            <Box display="flex" gap={2} mt={0.5}>
              <Chip
                size="small"
                variant="outlined"
                color={s3.has_invoice_number_region ? 'success' : 'error'}
                label={s3.has_invoice_number_region ? 'Number region' : 'No number region'}
              />
              <Chip
                size="small"
                variant="outlined"
                color={s3.has_invoice_amount_region ? 'success' : 'error'}
                label={s3.has_invoice_amount_region ? 'Amount region' : 'No amount region'}
              />
            </Box>
          )}
          {s3.vendor_contact_detail && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {s3.vendor_contact_detail.vendor_name} ({s3.vendor_contact_detail.vendor_id}) &middot;{' '}
              {s3.vendor_contact_detail.location}
            </Typography>
          )}
          {!s3.found && (
            <Typography variant="caption" color="text.secondary">
              {s2.matched ? 'Matched vendor has no VendorContactDetail record.' : 'No vendor match in step 2.'}
            </Typography>
          )}

          {s2.matched && (
            <Box mt={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadPdf}
                disabled={downloading}
              >
                {downloading
                  ? 'Uploading to S3...'
                  : step3Status === 'fail'
                    ? 'Upload Statement PDF for Region Detection'
                    : 'Re-upload Statement PDF for Debugging'}
              </Button>
              {downloadResult && (
                <Box mt={1} p={1.5} bgcolor="grey.50" borderRadius={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">Region Detection Info</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyRegionInfo}
                      color={regionInfoCopied ? 'success' : 'primary'}
                    >
                      {regionInfoCopied ? 'Copied!' : 'Copy for Claude'}
                    </Button>
                  </Box>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.8 }}>
                    <Box>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Vendor Name:{' '}
                      </Typography>
                      <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {downloadResult.vendor_name || '(unknown)'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Vendor ID:{' '}
                      </Typography>
                      <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {downloadResult.vendor_id || '(unknown)'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Location:{' '}
                      </Typography>
                      <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {downloadResult.location || '(unknown)'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Statement PDF:{' '}
                      </Typography>
                      <Typography
                        component="a"
                        href={downloadResult.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
                      >
                        {downloadResult.filename}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                    Click "Copy for Claude" and paste into Claude Code to run <code>/detect-regions</code>.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        {/* 4. Textract Line Items */}
        <Box>
          <SectionHeader
            title="Textract Line Items"
            status={step4Status}
            label={
              s4.has_line_items
                ? `${s4.invoices.reduce((sum, inv) => sum + inv.line_item_count, 0)} items`
                : s4.has_invoices
                  ? 'No line items'
                  : 'No invoices'
            }
          />
          {s4.invoices.length > 0 ? (
            s4.invoices.map((inv) => (
              <Typography key={inv.id} variant="caption" color="text.secondary" display="block">
                Invoice {inv.id}: {inv.line_item_count} line items ({inv.extraction_method || 'unknown method'})
              </Typography>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No vendor statements created from this email yet.
            </Typography>
          )}
        </Box>

        <Divider />

        {/* 5. Valid Statement Number & Amount */}
        <Box>
          <SectionHeader
            title="Valid Statement Number & Amount"
            status={step5Status}
            label={s5.has_valid_extraction ? 'Verified' : step5Status === 'na' ? 'N/A' : 'Missing data'}
          />
          {s5.invoices.length > 0 ? (
            <TableContainer>
              <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 1, fontSize: '0.75rem' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice ID</TableCell>
                    <TableCell>Statement #</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s5.invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.id}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PassFail pass={!!inv.statement_number} />
                          <Mono>{inv.statement_number || '(none)'}</Mono>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PassFail pass={inv.line_items_total != null} />
                          <Mono>
                            {inv.line_items_total != null
                              ? `$${parseFloat(String(inv.line_items_total)).toFixed(2)}`
                              : '(none)'}
                          </Mono>
                        </Box>
                      </TableCell>
                      <TableCell>{inv.extraction_method || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No invoices to validate.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default InvoiceWorkflowTracker;
